import { Component, ElementRef, OnInit, ViewChild, inject } from "@angular/core";
import { FormatType } from "@enums/FormatType";
import { ModuleType } from "@enums/ModuleType";
import { RendererType } from "@enums/RendererType";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { WorkspaceButtonConfig } from "@models/workspace/WorkspaceButtonConfig";
import { DocumentDataService } from "@services/http/document/DocumentDataService";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "./i18n/scope";
import { ToastrService } from "ngx-toastr";
import { firstValueFrom } from "rxjs";
import { download, nameof } from "@methods/CommonMethods";
import { DocumentModel } from "@models/documents/documentmodel";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { DocumentSharedModel } from "@app/audit/shared/data-models/document-shared-model";
import { DocumentsModel } from "@app/shared/models/DocumentsModel";
import { WorkspaceComponent } from "@app/shared/workspace/workspace.component";
import { DocumentManagerOldComponent } from "@app/shared/document-manager-old/document-manager-old.component";
import { DocumentHistoryComponent } from "@app/shared/document-manager/document-history/document-history.component";

declare var $: any;

@Component({
    selector: "shared-documents",
    templateUrl: "./shared-documents.component.html",
    styleUrls: ["./shared-documents.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [TranslatePipe, WorkspaceComponent, DocumentManagerOldComponent, DocumentHistoryComponent]
})
export class SharedDocumentsComponent implements OnInit {
    private permissionsService = inject(PermissionsService);
    private toastr = inject(ToastrService);
    private dataService = inject(DocumentDataService);
    protected promptService = inject(CerrixPromptService);
    private translation = injectTranslation(scope);

    moduleType = ModuleType.CD;
    tabID: string;
    tab: TabModel;
    data: Promise<DocumentSharedModel[]>;
    headerLookup: { [propName: string]: string };
    permissions: WorkspaceModulePermissionModel;
    filterConfigs: FilterConfig[] = [];
    rendererConfig: RendererConfig[] = [
        {
            textColumn: nameof<DocumentSharedModel>("updatedOn"),
            type: RendererType.Default,
            formatType: FormatType.DateTimeFormat,
        },
        {
            textColumn: nameof<DocumentSharedModel>("fileSize"),
            type: RendererType.Default,
            formatType: FormatType.FileSizeFormat,
        },
    ];

    buttonConfig: WorkspaceButtonConfig[];

    numberProps: string[] = [
        nameof<DocumentSharedModel>("version"),
        nameof<DocumentSharedModel>("fileSize"),
    ];
    dateProps: string[] = [nameof<DocumentSharedModel>("updatedOn")];

    @ViewChild("addModal", { static: true }) addModal: ElementRef;
    NewDocuments: DocumentModel[] = [];

    @ViewChild("historyModal", { static: true }) historyModal: ElementRef;
    documentHistoryGuid: string;

    protected disabled = false;

    constructor() {
        this.permissions = {
            canAdd: this.permissionsService.permissions.Documents.SharedUploader,
            canOpen: true,

            isModuleAdmin: false,
            canPublish: false,
            canSetSystemDefault: false,
        };
    }

    async ngOnInit(): Promise<void> {
        await this.translation.ready();

        const hasAccess = this.permissionsService.permissions.Documents.SharedViewer;
        if (!hasAccess) {
            this.toastr.error(this.translation.t("noAccess"));
            this.tab.close(false);
        }

        this.headerLookup = this.getHeaderLookup();
        this.buttonConfig = this.buildButtonConfig();

        this.data = firstValueFrom(this.dataService.GetSharedDocuments()).then(this.mapHeaders);
    }

    protected getFile(row: DocumentSharedModel) {
        this.promptService
            .confirm(
                this.translation.t("downloadButton"),
                this.translation.t("downloadConfirmMessage", { name: row.name }),
            )
            .onConfirm()
            .subscribe(() => {
                // Should retrieve url from one place (document service)
                download(`api/documents/${row.guid}/data`);
            });
    }

    protected openDocumentManager() {
        $(this.addModal.nativeElement).modal("show");
    }

    protected async addDocuments() {
        this.disabled = true;
        const model = new DocumentsModel();
        model.documents = this.NewDocuments;

        try {
            await this.dataService.StoreSharedDocuments(model);
        } catch (e) {
            this.disabled = false;
            throw e;
        }

        this.tab.refresh();
        this.NewDocuments = [];
        $(this.addModal.nativeElement).modal("hide");
    }

    private mapHeaders = (rows) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };

    private getHeaderLookup(): { [propName: string]: string } {
        const headerLookup = {};
        headerLookup[nameof<DocumentSharedModel>("guid")] = this.translation.t(
            "headers.guid",
        );
        headerLookup[nameof<DocumentSharedModel>("name")] = this.translation.t(
            "headers.name",
        );
        headerLookup[nameof<DocumentSharedModel>("description")] = this.translation.t(
            "headers.description",
        );
        headerLookup[nameof<DocumentSharedModel>("updatedBy")] = this.translation.t(
            "headers.updatedBy",
        );
        headerLookup[nameof<DocumentSharedModel>("updatedOn")] = this.translation.t(
            "headers.updatedOn",
        );
        headerLookup[nameof<DocumentSharedModel>("sourceTypeName")] = this.translation.t(
            "headers.source",
        );
        headerLookup[nameof<DocumentSharedModel>("typeName")] = this.translation.t(
            "headers.type",
        );
        headerLookup[nameof<DocumentSharedModel>("version")] = this.translation.t(
            "headers.version",
        );
        headerLookup[nameof<DocumentSharedModel>("fileSize")] = this.translation.t(
            "headers.fileSize",
        );

        return headerLookup;
    }

    private buildButtonConfig(): WorkspaceButtonConfig[] {
        return [
            {
                text: this.translation.t("downloadButton"),
                icon: "fa-download",
                rowRequired: true,
                clickEvent: (guid: string) => {
                    // Should retrieve url from one place (document service)
                    download(`api/documents/${guid}/data`);
                },
            },
            {
                text: this.translation.t("showHistoryButton"),
                icon: "fa-history",
                rowRequired: true,
                clickEvent: (guid: string) => {
                    this.documentHistoryGuid = guid;
                    $(this.historyModal.nativeElement).modal("show");
                },
            },
        ];
    }
}
